import React from 'react';
import { StyleSheet } from 'react-native';
import { ScrollViewWithHeaderTitle, theme } from '@sg/garnish';

import { useAuthFlow } from '@order/AuthFlow';
import { useIsLoggedIn } from '@order/AuthMachine';
import { AppFooter } from '@order/components';
import { useSweetpass } from '@order/hooks';
import { useTrackEffect } from '@order/Telemetry';

import type { SweetpassScreenProps } from '../../navigation/AppNavigation.props';
import { SweetpassScreenContent } from './subcomponents';

// ────────────────────────────────────────────────────────────────────────────────

export const SweetpassScreen = ({ navigation }: SweetpassScreenProps) => {
  const {
    isSweetpassFlagLoading,
    isSweetpassEnabled,
    isLoyaltyProfileLoading,
  } = useSweetpass();
  const isLoggedIn = useIsLoggedIn();
  const { startSignIn } = useAuthFlow();

  const isLoading = isSweetpassFlagLoading || isLoyaltyProfileLoading;
  const shouldRedirectToHome = !isLoading && !isSweetpassEnabled;

  // ─────────────────────────────────────────────────────────────────

  const handlePressSignIn = React.useCallback(() => {
    startSignIn('sweetpass');
  }, [startSignIn]);

  // ─────────────────────────────────────────────────────────────────

  useTrackEffect('sweetpass.view');

  React.useLayoutEffect(() => {
    if (shouldRedirectToHome) {
      navigation.navigate('HomeTab', { screen: 'Home' });
    }
  }, [navigation, shouldRedirectToHome]);

  // ─────────────────────────────────────────────────────────────────

  if (!isSweetpassEnabled) {
    return null;
  }

  return (
    <ScrollViewWithHeaderTitle
      testID="sweetpass.root.container"
      style={styles.scrollContainer}
      contentContainerStyle={styles.scrollContent}
    >
      <SweetpassScreenContent
        testID="sweetpass"
        isLoading={isLoading}
        isSignedIn={isLoggedIn}
        onPressSignIn={handlePressSignIn}
      />
      <AppFooter />
    </ScrollViewWithHeaderTitle>
  );
};

// ────────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  scrollContainer: {
    flexGrow: 1,
    backgroundColor: theme.colors.KHAKI_3,
  },
  scrollContent: {
    height: '100%',
  },
});
